import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { X, Camera, Upload, User, Loader2, Save, Mail, Phone, CreditCard, Check, Gift, Copy, Share2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const UserProfileModal = ({ 
  isOpen, 
  onClose, 
  user, 
  backendUrl,
  onProfileUpdate 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    profile_photo: ''
  });
  const [referralData, setReferralData] = useState(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Helper function to get full image URL
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    // If it's already a full URL or data URL, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    // If it's a relative URL starting with /api/, prepend backend URL
    if (imageUrl.startsWith('/api/')) {
      return `${backendUrl}${imageUrl}`;
    }
    // If it's just a path, prepend backend URL with /api
    return `${backendUrl}/api${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchUserProfile();
      fetchReferralCode();
    }
  }, [isOpen, user]);

  const fetchReferralCode = async () => {
    if (!user?.id) {
      setReferralData({ error: 'No user ID' });
      return;
    }
    
    try {
      const response = await axios.get(`${backendUrl}/api/referral/my-code/${user.id}`);
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral code:', error);
      // Set error state so UI doesn't show infinite loading
      setReferralData({ 
        error: true, 
        message: error.response?.data?.detail || 'Unable to load referral code' 
      });
    }
  };

  const copyReferralCode = async () => {
    if (referralData?.referral_code) {
      try {
        await navigator.clipboard.writeText(referralData.referral_code);
        toast({ title: "Copied!", description: `Referral code ${referralData.referral_code} copied to clipboard` });
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = referralData.referral_code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast({ title: "Copied!", description: `Referral code ${referralData.referral_code} copied to clipboard` });
      }
    }
  };

  const shareReferralLink = async () => {
    if (referralData?.referral_link) {
      const shareText = `Join J-Style Services with my referral code ${referralData.referral_code} and get $5 credit on your first ride! Sign up here: ${referralData.referral_link}`;
      
      // Try native share first (works on mobile)
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join J-Style Services',
            text: `Use my referral code ${referralData.referral_code} and get $5 credit on your first ride!`,
            url: referralData.referral_link
          });
          toast({ title: "Shared!", description: "Referral link shared successfully" });
        } catch (err) {
          // User cancelled or share failed - fallback to clipboard
          if (err.name !== 'AbortError') {
            await navigator.clipboard.writeText(shareText);
            toast({ title: "Link Copied!", description: "Referral link copied to clipboard. Paste it to share!" });
          }
        }
      } else {
        // Fallback for desktop - copy full message to clipboard
        try {
          await navigator.clipboard.writeText(shareText);
          toast({ title: "Link Copied!", description: "Referral link copied to clipboard. Paste it to share!" });
        } catch (err) {
          // Last resort - show the link in an alert
          toast({ 
            title: "Your Referral Link", 
            description: referralData.referral_link,
            duration: 10000
          });
        }
      }
    }
  };

  const fetchUserProfile = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/users/${user.id}/profile`);
      const data = response.data;
      
      // Get photo from response, fallback to localStorage
      const savedPhoto = data.profile_photo || localStorage.getItem(`user_photo_${user.id}`) || localStorage.getItem('user_photo');
      
      setProfileData({
        full_name: data.full_name || data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        profile_photo: savedPhoto || ''
      });
      
      // Update localStorage if we got photo from server
      if (data.profile_photo) {
        localStorage.setItem('user_photo', data.profile_photo);
        localStorage.setItem(`user_photo_${user.id}`, data.profile_photo);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Use local data if API fails
      const savedPhoto = localStorage.getItem(`user_photo_${user.id}`) || localStorage.getItem('user_photo') || user.profile_photo;
      setProfileData({
        full_name: user.name || user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        profile_photo: savedPhoto || ''
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    
    setIsSaving(true);
    try {
      await axios.put(`${backendUrl}/api/users/${user.id}/profile`, {
        full_name: profileData.full_name,
        phone: profileData.phone
      });
      
      toast({
        title: "Profile Updated!",
        description: "Your changes have been saved",
      });
      
      // Update parent component
      if (onProfileUpdate) {
        onProfileUpdate({
          ...user,
          name: profileData.full_name,
          full_name: profileData.full_name,
          phone: profileData.phone,
          profile_photo: profileData.profile_photo
        });
      }
      
      // Update localStorage
      localStorage.setItem('user_name', profileData.full_name);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Save Failed",
        description: error.response?.data?.detail || "Failed to save changes",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image under 5MB",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);
    setShowPhotoOptions(false);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${backendUrl}/api/users/${user.id}/upload/profile-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const imageUrl = response.data.url || response.data.photo_url;
      setProfileData(prev => ({ ...prev, profile_photo: imageUrl }));
      
      // Save to localStorage for persistence across sessions
      localStorage.setItem('user_photo', imageUrl);
      localStorage.setItem(`user_photo_${user.id}`, imageUrl);
      
      toast({
        title: "Photo Updated!",
        description: "Your profile picture has been saved",
      });

      if (onProfileUpdate) {
        onProfileUpdate({ ...user, profile_photo: imageUrl });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 text-white relative flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 left-4 p-2 hover:bg-white/10 rounded-full transition-colors flex items-center gap-1"
            title="Back to booking"
          >
            <span className="text-lg">←</span>
            <span className="text-sm">Back</span>
          </button>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
            title="Close"
          >
            <X size={20} />
          </button>
          <h2 className="text-2xl font-bold text-center">My Profile</h2>
          <p className="text-slate-300 text-sm mt-1 text-center">Manage your account settings</p>
        </div>

        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="animate-spin text-amber-500" size={32} />
          </div>
        ) : (
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Profile Photo Section */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div 
                  className="w-28 h-28 rounded-full overflow-hidden bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center cursor-pointer shadow-lg border-4 border-white group"
                  onClick={() => setShowPhotoOptions(!showPhotoOptions)}
                >
                  {profileData.profile_photo ? (
                    <img 
                      src={getFullImageUrl(profileData.profile_photo)} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="text-white" size={48} />
                  )}
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                    <Camera className="text-white" size={28} />
                  </div>

                  {/* Loading overlay */}
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-full">
                      <Loader2 className="text-white animate-spin" size={28} />
                    </div>
                  )}
                </div>

                {/* Camera Badge */}
                <button 
                  onClick={() => setShowPhotoOptions(!showPhotoOptions)}
                  className="absolute bottom-0 right-0 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg border-3 border-white hover:bg-amber-600 transition-colors"
                >
                  <Camera size={18} className="text-white" />
                </button>

                {/* Photo Options Dropdown */}
                {showPhotoOptions && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-xl shadow-xl border-2 p-2 z-50 min-w-[180px]">
                    <button
                      onClick={() => cameraInputRef.current?.click()}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-amber-50 text-left"
                    >
                      <Camera size={20} className="text-amber-600" />
                      <span className="font-medium">Take Photo</span>
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-amber-50 text-left"
                    >
                      <Upload size={20} className="text-amber-600" />
                      <span className="font-medium">Upload Photo</span>
                    </button>
                    <button
                      onClick={() => setShowPhotoOptions(false)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 text-left text-gray-500"
                    >
                      <X size={20} />
                      <span>Cancel</span>
                    </button>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-3">Tap to change photo</p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <User size={16} className="text-amber-600" />
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Enter your full name"
                  className="mt-1.5 border-2 focus:border-amber-500"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-amber-600" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  disabled
                  className="mt-1.5 border-2 bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Phone size={16} className="text-amber-600" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Enter your phone number"
                  className="mt-1.5 border-2 focus:border-amber-500"
                />
              </div>
            </div>

            {/* Payment Methods Card */}
            <div className="bg-slate-50 rounded-xl p-4 border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <CreditCard size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Payment Methods</p>
                    <p className="text-sm text-gray-500">Manage your cards</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  onClick={() => toast({ title: "Coming Soon!", description: "Card management will be available soon. Currently all payments are handled securely via Stripe checkout." })}
                >
                  Manage
                </Button>
              </div>
            </div>

            {/* Referral Program Card */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <Gift size={20} className="text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Refer Friends & Earn</p>
                  <p className="text-sm text-gray-600">Share your code, both get $5 credit!</p>
                </div>
              </div>
              
              {referralData && referralData.referral_code ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white rounded-lg px-4 py-3 border-2 border-dashed border-amber-300 font-mono text-lg font-bold text-center text-amber-700">
                      {referralData.referral_code}
                    </div>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={copyReferralCode}
                      className="border-amber-300 hover:bg-amber-100"
                      data-testid="copy-referral-btn"
                      title="Copy referral code"
                    >
                      <Copy size={18} className="text-amber-600" />
                    </Button>
                    <Button 
                      size="icon"
                      onClick={shareReferralLink}
                      className="bg-amber-500 hover:bg-amber-600"
                      data-testid="share-referral-btn"
                      title="Share referral link"
                    >
                      <Share2 size={18} className="text-white" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-white rounded-lg p-2 border">
                      <p className="text-2xl font-bold text-amber-600">{referralData.successful_referrals || 0}</p>
                      <p className="text-xs text-gray-500">Friends Joined</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 border">
                      <p className="text-2xl font-bold text-green-600">${(referralData.total_earned || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Total Earned</p>
                    </div>
                  </div>
                  
                  {referralData.available_credit > 0 && (
                    <div className="bg-green-100 rounded-lg p-2 text-center border border-green-300">
                      <p className="text-sm text-green-800">
                        <span className="font-bold">${referralData.available_credit.toFixed(2)}</span> credit available for your next ride!
                      </p>
                    </div>
                  )}
                </div>
              ) : referralData && referralData.error ? (
                <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                  <p className="text-gray-500 text-sm">{referralData.message || 'Referral code unavailable'}</p>
                  <button 
                    onClick={fetchReferralCode}
                    className="mt-2 text-amber-600 hover:text-amber-700 text-sm font-medium"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin text-amber-500" size={24} />
                </div>
              )}
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveProfile}
              disabled={isSaving}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-semibold py-6 text-lg shadow-lg"
            >
              {isSaving ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={20} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            
            {/* Back to Booking Button */}
            <Button
              variant="outline"
              className="w-full border-2 border-slate-300 hover:bg-slate-50"
              onClick={onClose}
            >
              ← Back to Booking
            </Button>
          </div>
        )}

        {/* Hidden File Inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoSelect}
          className="hidden"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handlePhotoSelect}
          className="hidden"
        />
      </Card>
    </div>
  );
};

export default UserProfileModal;
